import { useEffect, useMemo, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import {
  useGameStore,
  typedEmit,
  type MoveSubmitPayload,
  type SetupSubmitPayload,
  type ResignPayload,
  type PieceType,
  type Square
} from '@fog-chess/core';
import { Board, type BoardCallbacks } from '@/components/board';
import { ErrorBoundary } from '@/components/ErrorBoundary';

/**
 * GameScreen — Smart container for the Fog Chess match.
 *
 * Responsibilities:
 * - Reads matchId from route params
 * - Wires Board callbacks to socket events (move:submit, setup:submit, resign)
 * - Listens for game state updates (handled by GlobalMatchmakingListener + socket)
 * - Handles cleanup on unmount
 *
 * NON-OPTIMISTIC UI (Mandate C):
 * - User actions ONLY emit socket events + set submission lock
 * - Board state ONLY updates via server events (game:state, move:applied)
 * - No local optimistic updates to FogView
 */
export function GameScreen() {
  const { matchId } = useParams<{ matchId: string }>();
  const setMatchId = useGameStore((s) => s.setMatchId);
  const setSubmittingMove = useGameStore((s) => s.setSubmittingMove);
  const isSubmittingMove = useGameStore((s) => s.isSubmittingMove);

  // Initialize match ID on mount
  useEffect(() => {
    if (matchId) {
      setMatchId(matchId);
      // Join the match socket room
      typedEmit('match:join', { player_id: '', match_id: matchId });
    }
    return () => {
      setMatchId(null);
    };
  }, [matchId, setMatchId]);

  // Board callbacks — wired to socket with non-optimistic locking
  const boardCallbacks = useMemo<BoardCallbacks>(
    () => ({
      onMove: (from: Square, to: Square) => {
        // MANDATE C: Set lock BEFORE emitting - NO optimistic board update
        setSubmittingMove(true);
        
        const payload: MoveSubmitPayload = { from, to };
        typedEmit('move:submit', payload);
        
        // Lock will be released by:
        // 1. move:applied (success) - via socketController
        // 2. error:not_your_turn, error:turn_timeout, error:wrong_phase, error:invalid_payload, error:no_active_match, error:not_in_match - via socketController
      },
      onPromote: (from: Square, to: Square, promotion: PieceType) => {
        // MANDATE C: Set lock BEFORE emitting - NO optimistic board update
        setSubmittingMove(true);
        
        const payload: MoveSubmitPayload = { 
          from, 
          to, 
          promotion: promotion as 'b' | 'r' | 'q' | 'n' 
        };
        typedEmit('move:submit', payload);
      },
      onSubmitSetup: (placements: { square: Square; type: PieceType }[]) => {
        const viewerColor = useGameStore.getState().viewerColor;
        if (!viewerColor || !matchId) return;
        const payload: SetupSubmitPayload = { color: viewerColor, placements };
        typedEmit('setup:submit', payload);
      },
      onResign: () => {
        if (!matchId) return;
        const payload: ResignPayload = {};
        typedEmit('resign', payload);
      },
    }),
    [matchId, setSubmittingMove],
  );

  if (!matchId) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mx-auto max-w-4xl px-4 py-8 text-center"
      >
        <div className="rounded-xl border border-destructive/50 bg-destructive/10 p-8">
          <h1 className="text-2xl font-bold text-destructive">No Match ID</h1>
          <p className="mt-2 text-muted-foreground">
            Navigate to this page via matchmaking or a room invite.
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="mx-auto max-w-4xl px-4 py-8"
    >
      {/* Board-independent error boundary: a Board render crash is contained so
          the app shell (Header/Chat/Toaster) stays mounted. A full page reload —
          not a soft re-mount — guarantees a clean Zustand/socket rehydration. */}
      <ErrorBoundary fallback={(error) => <BoardCrashFallback error={error} />}>
        <Board
          callbacks={boardCallbacks}
          matchId={matchId}
          isSubmittingMove={isSubmittingMove}
        />
      </ErrorBoundary>
    </motion.div>
  );
}

/**
 * BoardCrashFallback — Stylized "Board Render Error — Please Refresh" card.
 * Rendered in place of the Board when its subtree throws. Kept here (co-located
 * with the only caller) so the app shell remains usable while the board region
 * is replaced. Recovery path is `window.location.reload()` because a soft
 * re-mount would re-crash against a poisoned store/socket payload.
 */
function BoardCrashFallback({ error }: { error: Error }): ReactNode {
  return (
    <div
      role="alert"
      className="mx-auto flex max-w-md flex-col items-center gap-3 rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-center"
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
        <AlertTriangle className="h-6 w-6 text-destructive" aria-hidden="true" />
      </div>
      <h2 className="text-lg font-semibold text-destructive">
        Board Render Error — Please Refresh
      </h2>
      <p className="text-sm text-muted-foreground">
        The board state could not be rendered safely. This is usually caused by
        a malformed match payload. Reloading the page restores a clean state.
      </p>
      <pre className="mt-1 w-full overflow-x-auto rounded-md bg-muted/60 px-3 py-2 text-left text-xs font-mono text-muted-foreground">
        {error.message || 'Unknown error'}
      </pre>
      <button
        type="button"
        onClick={() => {
          if (typeof window !== 'undefined') {
            window.location.reload();
          }
        }}
        className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-destructive/40 bg-destructive px-4 py-1.5 text-sm font-medium text-destructive-foreground transition-colors hover:bg-destructive/90"
        aria-label="Reload the page to restore the board"
      >
        <RotateCcw className="h-4 w-4" aria-hidden="true" />
        Reload board
      </button>
    </div>
  );
}